'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  name: string;
  description: string;
  priceRange: string;
  icon: string;
  specialization: boolean;
}

interface ServicesSectionProps {
  services: Service[];
  specializations: string[];
}

const ServicesSection = ({ services, specializations }: ServicesSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedService, setExpandedService] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-6">
        <div className="h-6 bg-muted rounded w-32 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const toggleService = (index: number) => {
    setExpandedService(expandedService === index ? null : index);
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6">
      <div className="mb-6">
        <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">
          Services Offered
        </h2>
        <div className="flex items-center space-x-2 flex-wrap gap-2">
          <span className="font-caption text-xs text-text-secondary">Specializes in:</span>
          {specializations.map((spec, index) => (
            <span
              key={index}
              className="inline-flex items-center space-x-1 bg-primary/10 rounded-full px-3 py-1"
            >
              <Icon name="CheckCircleIcon" size={14} className="text-primary" variant="solid" />
              <span className="font-caption text-xs font-medium text-primary">
                {spec}
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service, index) => (
          <div
            key={index}
            className="border border-border rounded-lg p-4 transition-smooth hover:border-primary hover:shadow-elevation-sm"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3 flex-1">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                  <Icon name={service.icon as any} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-body text-sm font-semibold text-text-primary mb-1 truncate">
                    {service.name}
                  </h3>
                  <p className="font-mono text-xs text-accent font-medium">
                    {service.priceRange}
                  </p>
                </div>
              </div>
              {service.specialization && (
                <Icon name="StarIcon" size={16} className="text-accent flex-shrink-0" variant="solid" />
              )}
            </div>

            <p className="font-body text-xs text-text-secondary leading-relaxed mb-3">
              {expandedService === index
                ? service.description
                : `${service.description.substring(0, 80)}...`}
            </p>

            <button
              onClick={() => toggleService(index)}
              className="font-caption text-xs text-primary hover:underline"
            >
              {expandedService === index ? 'Show Less' : 'Learn More'}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg bg-muted p-4">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-caption text-xs font-medium text-text-primary mb-1">
              Pricing Information
            </p>
            <p className="font-body text-xs text-text-secondary leading-relaxed">
              Prices shown are estimates and may vary based on vehicle condition, parts availability, and labor requirements. Request a detailed quote for accurate pricing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;